import { type ReactNode, useEffect, useRef } from "react";
import {
  ClipboardCheck,
  ListTodo,
  Search,
  SlidersHorizontal,
  Truck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useBrowserBackDismiss } from "@/hooks/use-browser-back-dismiss";
import { cn } from "@/lib/utils";

export type MobileDispatchWorkspaceKind =
  | "fleet"
  | "tasks"
  | "assign"
  | "lookup"
  | "filters";

const TITLES: Record<MobileDispatchWorkspaceKind, string> = {
  fleet: "Fleet",
  tasks: "Tasks",
  assign: "Assign tasks",
  lookup: "Lookup",
  filters: "Dispatch filters",
};

export function MobileDispatchWorkspace({
  active,
  stagedCount,
  fleetCount,
  taskCount,
  onActiveChange,
  children,
}: {
  active: MobileDispatchWorkspaceKind | null;
  stagedCount: number;
  fleetCount: number;
  taskCount: number;
  onActiveChange: (active: MobileDispatchWorkspaceKind | null) => void;
  children: ReactNode;
}) {
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const previousActiveRef = useRef(active);
  useBrowserBackDismiss({
    open: active !== null,
    onDismiss: () => onActiveChange(null),
  });

  useEffect(() => {
    const wasOpen = previousActiveRef.current !== null;
    previousActiveRef.current = active;
    if (!wasOpen || active !== null) return;
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    if (target?.isConnected) target.focus();
  }, [active]);

  const openWorkspace = (next: MobileDispatchWorkspaceKind) => {
    if (active === null && document.activeElement instanceof HTMLElement) {
      returnFocusRef.current = document.activeElement;
    }
    onActiveChange(active === next ? null : next);
  };

  return (
    <>
      <nav
        aria-label="Mobile dispatch workspace"
        className="z-30 grid h-[calc(4rem+env(safe-area-inset-bottom))] shrink-0 grid-cols-5 items-start border-t border-zinc-200 bg-white px-1 pb-[env(safe-area-inset-bottom)] pt-1 shadow-[0_-2px_8px_rgba(15,15,15,0.06)]"
      >
        <WorkspaceButton
          label="Fleet"
          ariaLabel="Open Fleet"
          active={active === "fleet"}
          onClick={() => openWorkspace("fleet")}
          icon={<Truck />}
        />
        <WorkspaceButton
          label="Tasks"
          ariaLabel="Open Tasks"
          active={active === "tasks"}
          onClick={() => openWorkspace("tasks")}
          icon={<ListTodo />}
        />
        <WorkspaceButton
          label="Work"
          ariaLabel={`Open assignment, ${stagedCount} selected`}
          active={active === "assign"}
          disabled={stagedCount === 0}
          onClick={() => openWorkspace("assign")}
          icon={<ClipboardCheck />}
          badge={stagedCount || undefined}
        />
        <WorkspaceButton
          label="Lookup"
          ariaLabel="Open Lookup"
          active={active === "lookup"}
          onClick={() => openWorkspace("lookup")}
          icon={<Search />}
        />
        <WorkspaceButton
          label="Filters"
          ariaLabel="Open Filters"
          active={active === "filters"}
          onClick={() => openWorkspace("filters")}
          icon={<SlidersHorizontal />}
        />
      </nav>

      <Sheet
        open={active !== null}
        onOpenChange={(open) => !open && onActiveChange(null)}
      >
        {active && (
          <SheetContent
            side="bottom"
            aria-describedby={undefined}
            closeLabel={
              active === "assign"
                ? "Close assignment workspace"
                : `Close ${TITLES[active]}`
            }
            className="flex h-[min(84dvh,46rem)] w-full flex-col gap-0 overflow-hidden rounded-t-2xl p-0"
          >
            <SheetHeader className="flex h-12 shrink-0 flex-row items-center border-b px-4 pr-14 text-left">
              <SheetTitle className="flex items-center gap-2 text-base">
                {TITLES[active]}
                {active === "fleet" && (
                  <Badge variant="secondary" aria-hidden="true">
                    {fleetCount}
                  </Badge>
                )}
                {active === "tasks" && (
                  <Badge variant="secondary" aria-hidden="true">
                    {taskCount}
                  </Badge>
                )}
                {active === "assign" && (
                  <Badge variant="secondary" aria-hidden="true">
                    {stagedCount} selected
                  </Badge>
                )}
              </SheetTitle>
              {active === "tasks" && stagedCount > 0 && (
                <Button
                  type="button"
                  size="sm"
                  className="ml-auto h-11"
                  aria-label={`Review ${stagedCount} selected Task${stagedCount === 1 ? "" : "s"}`}
                  onClick={() => onActiveChange("assign")}
                >
                  Review {stagedCount}
                </Button>
              )}
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
          </SheetContent>
        )}
      </Sheet>
    </>
  );
}

function WorkspaceButton({
  label,
  ariaLabel,
  active,
  disabled,
  onClick,
  icon,
  badge,
}: {
  label: string;
  ariaLabel: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: ReactNode;
  badge?: number;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      aria-label={ariaLabel}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative h-14 min-w-0 flex-col gap-0.5 rounded-lg px-1 text-[10px] text-zinc-600 [&_svg]:size-4",
        active && "bg-indigo-50 text-indigo-700"
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
      {badge !== undefined && (
        <Badge className="absolute right-1 top-1 h-4 min-w-4 px-1 text-[9px]">
          {badge}
        </Badge>
      )}
    </Button>
  );
}
